import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AboutSettings = () => {
  const [form, setForm] = useState({
    subtitle: '',
    titleLine1: '',
    titleLine2: '',
    descriptionText: '',
    aboutImageUrl: '',
    badgeLabel: '',
    badgeValue: '',
    stat1Label: '',
    stat1Value: '',
    stat2Label: '',
    stat2Value: '',
    stat3Label: '',
    stat3Value: '',
    featuresText: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchAbout = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/about`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { about } = res.data;
        if (about) {
          setForm({
            subtitle: about.subtitle || '',
            titleLine1: about.titleLine1 || '',
            titleLine2: about.titleLine2 || '',
            descriptionText: about.descriptionText || '',
            aboutImageUrl: about.aboutImageUrl || '',
            badgeLabel: about.badgeLabel || '',
            badgeValue: about.badgeValue || '',
            stat1Label: about.stat1Label || '',
            stat1Value: about.stat1Value || '',
            stat2Label: about.stat2Label || '',
            stat2Value: about.stat2Value || '',
            stat3Label: about.stat3Label || '',
            stat3Value: about.stat3Value || '',
            featuresText: Array.isArray(about.features) ? about.features.join('\n') : '',
          });
        }
      } catch {
        // ignore, will use defaults
      } finally {
        setLoading(false);
      }
    };

    fetchAbout();
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    setError('');
    setSuccess('');
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      return;
    }

    try {
      const data = new FormData();
      data.append('image', file);
      const res = await axios.post(`${API_BASE_URL}/api/admin/upload-image`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.data?.url) {
        updateField('aboutImageUrl', res.data.url);
        setSuccess('تم رفع صورة قسم من نحن بنجاح.');
      }
    } catch (err) {
      const message =
        err.response?.data?.message || 'فشل رفع الصورة. تأكد من أن الملف صورة أقل من 5MB.';
      setError(message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      setSaving(false);
      return;
    }

    const features = form.featuresText
      ? form.featuresText.split('\n').map((s) => s.trim()).filter(Boolean)
      : [];

    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/about`,
        {
          ...form,
          stat1Value: form.stat1Value || null,
          stat2Value: form.stat2Value || null,
          stat3Value: form.stat3Value || null,
          features,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSuccess('تم حفظ إعدادات قسم من نحن بنجاح.');
    } catch (err) {
      const message = err.response?.data?.message || 'حدث خطأ أثناء الحفظ.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p>جاري تحميل إعدادات قسم من نحن...</p>;
  }

  return (
    <div className="admin-section">
      <h2 className="admin-section-title">إعدادات قسم من نحن</h2>
      <p className="admin-section-subtitle">
        يمكنك هنا تعديل محتوى قسم من نحن في الصفحة الرئيسية، بما في ذلك العنوان، النص، النقاط
        الرئيسية، وصورة الفريق.
      </p>

      {error && <div className="admin-section-error">{error}</div>}
      {success && <div className="admin-section-success">{success}</div>}

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>النص الصغير (Sub-title)</label>
          <input
            type="text"
            value={form.subtitle}
            onChange={(e) => updateField('subtitle', e.target.value)}
            placeholder="من نحن"
          />
        </div>
        <div className="admin-field">
          <label>العنوان - السطر الأول</label>
          <input
            type="text"
            value={form.titleLine1}
            onChange={(e) => updateField('titleLine1', e.target.value)}
            placeholder="في نبض كير نقدم حلولاً استشارية مبتكرة"
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>العنوان - السطر الثاني</label>
          <input
            type="text"
            value={form.titleLine2}
            onChange={(e) => updateField('titleLine2', e.target.value)}
            placeholder="لضمان امتثال ونمو أعمالك"
          />
        </div>
        <div className="admin-field">
          <label>النص الوصفي</label>
          <input
            type="text"
            value={form.descriptionText}
            onChange={(e) => updateField('descriptionText', e.target.value)}
            placeholder="نبض كير هي شريكك الموثوق في المملكة العربية السعودية..."
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>رابط صورة قسم من نحن</label>
          <input
            type="text"
            value={form.aboutImageUrl}
            onChange={(e) => updateField('aboutImageUrl', e.target.value)}
            placeholder="/uploads/about.webp أو رابط خارجي"
          />
          <small>يمكنك رفع صورة جديدة أو استخدام رابط خارجي.</small>
          <div style={{ marginTop: '0.5rem' }}>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleUploadImage(e.target.files?.[0] || null)}
            />
          </div>
        </div>

        <div className="admin-field">
          <label>البادج العائم (النص والقيمة)</label>
          <input
            type="text"
            value={form.badgeValue}
            onChange={(e) => updateField('badgeValue', e.target.value)}
            placeholder="100%"
          />
          <input
            type="text"
            style={{ marginTop: '0.4rem' }}
            value={form.badgeLabel}
            onChange={(e) => updateField('badgeLabel', e.target.value)}
            placeholder="نسبة رضا العملاء"
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>إحصائية 1 - العنوان والقيمة</label>
          <input
            type="text"
            value={form.stat1Label}
            onChange={(e) => updateField('stat1Label', e.target.value)}
            placeholder="مشروع ناجح"
          />
          <input
            type="text"
            style={{ marginTop: '0.4rem' }}
            value={form.stat1Value}
            onChange={(e) => updateField('stat1Value', e.target.value)}
            placeholder="2.5k+"
          />
        </div>

        <div className="admin-field">
          <label>إحصائية 2 - العنوان والقيمة</label>
          <input
            type="text"
            value={form.stat2Label}
            onChange={(e) => updateField('stat2Label', e.target.value)}
            placeholder="خبير استشاري"
          />
          <input
            type="text"
            style={{ marginTop: '0.4rem' }}
            value={form.stat2Value}
            onChange={(e) => updateField('stat2Value', e.target.value)}
            placeholder="15+"
          />
        </div>
      </div>

      <div className="admin-grid-2">
        <div className="admin-field">
          <label>إحصائية 3 - العنوان والقيمة</label>
          <input
            type="text"
            value={form.stat3Label}
            onChange={(e) => updateField('stat3Label', e.target.value)}
            placeholder="نسبة القبول"
          />
          <input
            type="text"
            style={{ marginTop: '0.4rem' }}
            value={form.stat3Value}
            onChange={(e) => updateField('stat3Value', e.target.value)}
            placeholder="100%"
          />
        </div>

        <div className="admin-field">
          <label>النقاط الرئيسية (كل سطر يمثل نقطة مميزة)</label>
          <textarea
            rows={6}
            value={form.featuresText}
            onChange={(e) => updateField('featuresText', e.target.value)}
            style={{ resize: 'vertical' }}
            placeholder={`فريق استشاري محترف\nسرعة في الإنجاز\nموثوقية وأمان\nدقة في التوثيق\nدعم مستمر 24/7\nضمان الرضا`}
          />
        </div>
      </div>

      <div className="admin-actions">
        <button
          type="button"
          className="admin-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
        </button>
      </div>
    </div>
  );
};

export default AboutSettings;

